import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon, CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { useCreateBusinessOrderMutationApi } from "src/hooks/useB2bTrading";
import {
  useExchangeMutationApi,
  useGetUserPaymentMethods,
  useP2PMutationApi,
} from "src/hooks/useTrading";
import { useUserData } from "src/hooks/useUserData";
import {
  BuyerPaymentCategory,
  BuyerPaymentMethodChoice,
  classifyBuyerPaymentMethod,
  getCollectionCurrency,
  getLocalMobileMoneyCurrency,
  getPhonePlaceholder,
  normalizeMobileMoneyPhone,
  resolveCollectionRouteForCategory,
  supportsMobileMoneyForCountry,
} from "src/helpers/tradeFlow";
import { checkDepositStatus, previewProviderCharges } from "src/services";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import LoadingIndicator from "./LoadingIndicator";

type Step = "amount" | "payment" | "mobile_money" | "review" | "processing" | "result";

const STEPS: { key: Step; label: string }[] = [
  { key: "amount", label: "Amount" },
  { key: "payment", label: "Payment" },
  { key: "review", label: "Review" },
];

type NetworkProvider = { code: string; name: string };

const GHANA_PROVIDERS: NetworkProvider[] = [
  { code: "MTN", name: "MTN Mobile Money" },
  { code: "VOD", name: "Vodafone Cash" },
  { code: "AIR", name: "AirtelTigo Money" },
];

const KENYA_PROVIDERS: NetworkProvider[] = [
  { code: "MPESA", name: "M-PESA" },
];

const KENYA_CODES = new Set(["KE", "KEN", "KENYA"]);
const GHANA_CODES = new Set(["GH", "GHA", "GHANA"]);

const getProvidersForCountry = (country: string): NetworkProvider[] => {
  const c = country.trim().toUpperCase();
  if (GHANA_CODES.has(c)) return GHANA_PROVIDERS;
  if (KENYA_CODES.has(c)) return KENYA_PROVIDERS;
  return [];
};

type PaymentMethodLike = {
  id: string;
  name?: string;
  payment_option?: { name?: string; slug?: string; type?: string };
  account_details?: { provider_name?: string };
};

type BuyerMethodChoice = BuyerPaymentMethodChoice & {
  category: BuyerPaymentCategory;
  methodId?: string;
};

const getMethodName = (m: PaymentMethodLike) =>
  String(m?.payment_option?.name || m?.name || "").trim();

const getMethodType = (m: PaymentMethodLike) =>
  String(m?.payment_option?.type || "").trim().toLowerCase();

const getInitialCurrency = (request: any) =>
  String(request?.active_currency || request?.currency_codes?.[0] || "").toUpperCase();

const asOfferMethods = (methods: any[]): PaymentMethodLike[] =>
  Array.isArray(methods)
    ? methods.filter((m) => Boolean(m?.id)).map((m) => ({
        id: String(m.id),
        name: m?.name,
        payment_option: m?.payment_option || {},
        account_details: m?.account_details || {},
      }))
    : [];

const buildMethodChoices = ({
  buyerCountry,
  request,
  offerMethods,
}: {
  buyerCountry: string;
  request: any;
  offerMethods: PaymentMethodLike[];
}): {
  choices: BuyerMethodChoice[];
  mobileMoneyMethods: PaymentMethodLike[];
  cardMethods: PaymentMethodLike[];
} => {
  const localMobileMoneyCurrency = getLocalMobileMoneyCurrency(buyerCountry);
  const offerCurrencies = (request?.currency_codes || []).map((c: string) =>
    String(c || "").toUpperCase(),
  );
  const canUseLocalMobileMoney =
    supportsMobileMoneyForCountry(buyerCountry) &&
    Boolean(localMobileMoneyCurrency) &&
    offerCurrencies.includes(localMobileMoneyCurrency);

  const mobileMoneyMethods = offerMethods.filter((m) => {
    const category = classifyBuyerPaymentMethod(m);
    const optionType = getMethodType(m);
    return category === "mobile_money" || optionType === "momo";
  });

  const explicitCardMethods = offerMethods.filter((m) => {
    const category = classifyBuyerPaymentMethod(m);
    if (category === "card") return true;
    return getMethodType(m) === "card";
  });

  const fallbackCardMethods =
    explicitCardMethods.length > 0
      ? explicitCardMethods
      : offerMethods.filter((m) => !mobileMoneyMethods.some((mm) => mm.id === m.id));

  const choices: BuyerMethodChoice[] = [];

  if (canUseLocalMobileMoney && getProvidersForCountry(buyerCountry).length > 0) {
    choices.push({
      key: "mobile_money",
      title: "Mobile Money",
      description: "Pay with your mobile money wallet",
      category: "mobile_money",
      routeLabel: resolveCollectionRouteForCategory("mobile_money", buyerCountry),
    });
  }

  choices.push({
    key: "card",
    title: "Debit / Credit Card",
    description: "Pay with Visa, Mastercard, or other cards",
    category: "card",
    routeLabel: resolveCollectionRouteForCategory("card", buyerCountry),
    methodId: fallbackCardMethods[0]?.id,
  });

  return { choices, mobileMoneyMethods, cardMethods: fallbackCardMethods };
};

/* ─── Step Progress Indicator ─── */

const StepIndicator = ({ currentStep }: { currentStep: Step }) => {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);
  const effectiveIndex =
    currentStep === "mobile_money"
      ? 1
      : currentStep === "processing" || currentStep === "result"
        ? STEPS.length
        : currentIndex;

  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const isComplete = i < effectiveIndex;
        const isCurrent = i === effectiveIndex;
        return (
          <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                  isComplete
                    ? "bg-emerald-500 text-white"
                    : isCurrent
                      ? "bg-red-500 text-white ring-4 ring-red-100"
                      : "bg-gray-100 text-gray-400"
                }`}
              >
                {isComplete ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`mt-1.5 text-[11px] font-medium ${
                  isComplete ? "text-emerald-600" : isCurrent ? "text-red-600" : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`mx-2 h-0.5 w-12 rounded-full transition-all duration-300 ${
                  i < effectiveIndex ? "bg-emerald-400" : "bg-gray-200"
                }`}
                style={{ marginTop: "-1rem" }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── Summary Row ─── */

const SummaryRow = ({ label, value, accent }: { label: string; value: string; accent?: boolean }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className={`text-sm font-semibold ${accent ? "text-red-600" : "text-gray-900"}`}>
      {value}
    </span>
  </div>
);

/* ─── Main Component ─── */

const AddTradeOrders = ({
  open,
  handleClose,
  request,
  businessId,
}: {
  open: boolean;
  handleClose: () => void;
  request: any;
  businessId?: any;
}) => {
  const [step, setStep] = useState<Step>("amount");
  const [buyAmount, setBuyAmount] = useState("");
  const [targetCurrency, setTargetCurrency] = useState("");
  const [showErrorText, setShowErrorText] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<BuyerPaymentCategory | "">("");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [selectedNetworkCode, setSelectedNetworkCode] = useState("");
  const [mobileMoneyPhone, setMobileMoneyPhone] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [feePreview, setFeePreview] = useState<{ charge_amount: number; total_amount: number; fee_applied: boolean; charge_percentage?: string } | null>(null);
  const [depositCheckStatus, setDepositCheckStatus] = useState<"idle" | "checking" | "completed" | "pending" | "failed">("idle");
  const [depositCheckMessage, setDepositCheckMessage] = useState("");

  const { data: storedMethodsResponse } = useGetUserPaymentMethods();
  const { userData } = useUserData();
  const convert = useExchangeMutationApi();
  const createOrder = useP2PMutationApi();
  const createBusinessTradeOrder = useCreateBusinessOrderMutationApi();

  const isBusiness = Boolean(businessId);
  const limitCurrency = String(request?.active_currency || "USD").toUpperCase();
  const minAmountUsd = Number(request?.min_amount ?? request?.min_amount_usd ?? 0);
  const maxAmountUsd = Number(request?.max_amount ?? request?.max_amount_usd ?? 0);
  const buyerCountry =
    String(userData?.country_code || userData?.country || request?.region || "").trim().toUpperCase();
  const localMobileMoneyCurrency = getLocalMobileMoneyCurrency(buyerCountry);
  const availableCurrencyOptions = useMemo(() => {
    const base = (request?.currency_codes || []).map((v: string) => String(v || "").toUpperCase());
    const collectionCurrency = selectedCategory
      ? getCollectionCurrency(selectedCategory as BuyerPaymentCategory, buyerCountry)
      : null;
    if (collectionCurrency && !base.includes(collectionCurrency)) {
      return [...base, collectionCurrency];
    }
    return base;
  }, [request?.currency_codes, selectedCategory, buyerCountry]);

  const offerMethods = useMemo(
    () => asOfferMethods(request?.payment_methods || storedMethodsResponse?.results || []),
    [request?.payment_methods, storedMethodsResponse?.results],
  );

  const { choices, cardMethods } = useMemo(
    () => buildMethodChoices({ buyerCountry, request, offerMethods }),
    [buyerCountry, request, offerMethods],
  );

  const selectedChoice = choices.find((c) => c.category === selectedCategory);
  const isSubmitting = createOrder.isPending || createBusinessTradeOrder.isPending;
  const orderResponse = createBusinessTradeOrder.data || createOrder.data;
  const rate = Number(request?.conversion_rate_usd || 0);
  const canFallbackConvert = rate > 0 && Number(buyAmount) > 0 && targetCurrency === limitCurrency;
  const convertedAmount =
    convert.data?.converted_amount ??
    (canFallbackConvert ? Number(buyAmount) / rate : null);

  const providerPayment = orderResponse?.provider_payment;
  const hasRedirect = Boolean(providerPayment?.redirect_url);
  const isApproveOnPhone = Boolean(providerPayment) && !hasRedirect;

  const sellerSettlementCurrency =
    orderResponse?.seller_settlement_currency ||
    orderResponse?.provider_flow?.seller_settlement_currency ||
    targetCurrency;

  const routePreview =
    selectedChoice?.routeLabel ||
    (choices.length === 1 ? choices[0].routeLabel : "");

  // ─── Reset on open ───
  useEffect(() => {
    if (!open) return;
    setStep("amount");
    setBuyAmount("");
    setTargetCurrency(getInitialCurrency(request));
    setShowErrorText(false);
    setSelectedCategory("");
    setSelectedPaymentMethodId("");
    setSelectedNetworkCode("");
    setMobileMoneyPhone(String(userData?.phone || ""));
    setSubmissionError("");
    setFeePreview(null);
    setDepositCheckStatus("idle");
    setDepositCheckMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request, userData?.phone]);

  // ─── Validate amount ───
  useEffect(() => {
    const amount = Number(buyAmount);
    const hasAmount = buyAmount.trim() !== "";
    const limitApplies = targetCurrency === limitCurrency;
    setShowErrorText(
      hasAmount &&
        (Number.isNaN(amount) ||
          (limitApplies && (amount < minAmountUsd || amount > maxAmountUsd))),
    );
  }, [buyAmount, minAmountUsd, maxAmountUsd, targetCurrency, limitCurrency]);

  // ─── Live conversion ───
  useEffect(() => {
    if (!buyAmount || !targetCurrency || showErrorText) return;
    convert.mutate({
      amount: buyAmount,
      base_currency_code: targetCurrency,
      target_currency_code: "AKL",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyAmount, targetCurrency, showErrorText]);

  // ─── Fetch fee preview when entering review step ───
  useEffect(() => {
    if (step !== "review" || !buyAmount || !routePreview) return;
    const provider = routePreview.toLowerCase().includes("synthera") ? "synthera" : "korba";
    previewProviderCharges(provider, "collection", Number(buyAmount))
      .then(setFeePreview)
      .catch(() => setFeePreview(null));
  }, [step, buyAmount, routePreview]);

  // ─── Auto-select single choice ───
  useEffect(() => {
    if (choices.length === 1 && !selectedCategory) {
      setSelectedCategory(choices[0].category);
    }
  }, [choices, selectedCategory]);

  // ─── Sync payment method id & collection currency ───
  useEffect(() => {
    if (!selectedCategory) {
      setSelectedPaymentMethodId("");
      return;
    }
    if (selectedCategory === "card") {
      setSelectedNetworkCode("");
      setSelectedPaymentMethodId(cardMethods[0]?.id || "");
    } else {
      setSelectedPaymentMethodId("");
    }
    const collectionCurrency = getCollectionCurrency(selectedCategory as BuyerPaymentCategory, buyerCountry);
    if (collectionCurrency && targetCurrency !== collectionCurrency) {
      setTargetCurrency(collectionCurrency);
      setBuyAmount("");
      setStep("amount");
    }
  }, [selectedCategory, selectedNetworkCode, cardMethods, buyerCountry, targetCurrency]);

  const networkProviders = useMemo(() => getProvidersForCountry(buyerCountry), [buyerCountry]);

  // ─── Auto-select single network provider ───
  useEffect(() => {
    if (selectedCategory !== "mobile_money" || step !== "mobile_money") return;
    if (selectedNetworkCode || networkProviders.length !== 1) return;
    setSelectedNetworkCode(networkProviders[0].code);
  }, [selectedCategory, selectedNetworkCode, step, networkProviders]);

  // ─── Validation flags ───
  const detailsValid =
    buyAmount.trim() !== "" && targetCurrency.trim() !== "" && !showErrorText && Number(buyAmount) > 0;

  const mobileMoneyDetailsValid =
    selectedCategory !== "mobile_money" ||
    (selectedNetworkCode.trim() !== "" && mobileMoneyPhone.trim() !== "");

  const canContinuePayment =
    selectedCategory !== "" &&
    (selectedCategory === "mobile_money" || selectedCategory === "card" || selectedPaymentMethodId.trim() !== "");

  // ─── Submit ───
  const submitOrder = () => {
    if (!detailsValid || !canContinuePayment || !mobileMoneyDetailsValid) return;

    const payload: Record<string, any> = {
      trade_request: request?.id,
      amount: buyAmount,
      currency_code: targetCurrency,
      seller_settlement_currency_code: targetCurrency,
      payment_method_category: selectedCategory,
      buyer_country: userData?.country_code || userData?.country,
      buyer_phone: selectedCategory === "mobile_money" ? normalizeMobileMoneyPhone(mobileMoneyPhone, buyerCountry) : userData?.phone,
      buyer_email: userData?.email,
      buyer_first_name: userData?.firstname || userData?.first_name || "",
      buyer_last_name: userData?.lastname || userData?.last_name || "",
      ...(selectedPaymentMethodId ? { payment_method: selectedPaymentMethodId } : {}),
      ...(selectedCategory === "mobile_money" && selectedNetworkCode
        ? { network_code: selectedNetworkCode }
        : {}),
    };

    const mutation = isBusiness ? createBusinessTradeOrder : createOrder;
    const finalPayload = isBusiness ? { ...payload, business_id: businessId } : payload;

    setSubmissionError("");
    setStep("processing");
    mutation.mutate(finalPayload, {
      onSuccess: () => setStep("result"),
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Unable to start payment. Please try again.";
        setSubmissionError(message);
        setStep("review");
      },
    });
  };

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay" />
        <Dialog.Content
          className="DialogContent"
          style={{ width: "min(720px, calc(100vw - 2rem))", maxHeight: "calc(100vh - 2rem)" }}
        >
          <Dialog.Close asChild>
            <button className="IconButton" aria-label="Close" onClick={handleClose}>
              <Cross2Icon />
            </button>
          </Dialog.Close>

          {/* ─── Header ─── */}
          <div className="mb-2">
            <Dialog.Title className="DialogTitle" style={{ marginBottom: 4 }}>
              {step === "result" ? "Order Status" : "Buy AKL"}
            </Dialog.Title>
            <p className="text-sm text-gray-500">
              {request?.trading_name && (
                <>
                  from <span className="font-medium text-gray-700">{request.trading_name}</span>
                  {" · "}
                </>
              )}
              Rate: {request?.conversion_rate_usd} USD/AKL
            </p>
          </div>

          {step !== "processing" && step !== "result" && <StepIndicator currentStep={step} />}

          {/* ═══════════════════════════ STEP: AMOUNT ═══════════════════════════ */}
          {step === "amount" && (
            <div className="space-y-6">
              {selectedCategory === "mobile_money" && localMobileMoneyCurrency && (
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3">
                  <p className="text-xs text-blue-700">
                    Mobile Money payments are collected in <span className="font-semibold">{localMobileMoneyCurrency}</span>. Please enter your amount in {localMobileMoneyCurrency}.
                  </p>
                </div>
              )}
              {selectedCategory === "card" && getCollectionCurrency("card", buyerCountry) && (
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3">
                  <p className="text-xs text-blue-700">
                    Card payments are processed in <span className="font-semibold">{getCollectionCurrency("card", buyerCountry)}</span>. Please enter your amount in {getCollectionCurrency("card", buyerCountry)}.
                  </p>
                </div>
              )}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5">
                <label className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">
                  You pay
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={buyAmount}
                    onChange={(e) => setBuyAmount(e.target.value)}
                    className="flex-1 text-2xl font-semibold border-0 bg-transparent p-0 focus-visible:ring-0 h-auto"
                    style={{ fontSize: "1.75rem" }}
                  />
                  <Select value={targetCurrency} onValueChange={setTargetCurrency}>
                    <SelectTrigger className="w-[110px] bg-white border-gray-200 rounded-xl">
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCurrencyOptions.map((v: string) => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {showErrorText && (
                  <p className="mt-2 text-xs text-red-500">
                    Amount must be between {minAmountUsd} and {maxAmountUsd} {limitCurrency}
                  </p>
                )}
              </div>

              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5">
                <label className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">
                  You receive
                </label>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-emerald-700" style={{ fontSize: "1.75rem" }}>
                    {convertedAmount
                      ? convertedAmount < 0.01
                        ? convertedAmount.toFixed(6)
                        : convertedAmount.toFixed(2)
                      : "—"}
                  </span>
                  <span className="rounded-xl bg-emerald-100 px-3 py-1.5 text-sm font-bold text-emerald-700">
                    AKL
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <SummaryRow label="Rate" value={`1 AKL = ${request?.conversion_rate_usd} USD`} />
                <SummaryRow label="Limit" value={`${minAmountUsd} – ${maxAmountUsd} ${limitCurrency}`} />
                {request?.region && <SummaryRow label="Region" value={request.region} />}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => setStep("payment")}
                  disabled={!detailsValid}
                  className="rounded-xl bg-red-500 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-600 disabled:opacity-40"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════ STEP: PAYMENT METHOD ═══════════════════════════ */}
          {step === "payment" && (
            <div className="space-y-6">
              <p className="text-sm text-gray-500">How would you like to pay?</p>

              <div className="space-y-3">
                {choices.length ? (
                  choices.map((choice) => (
                    <button
                      type="button"
                      key={choice.key}
                      onClick={() => setSelectedCategory(choice.category)}
                      className={`group w-full rounded-2xl border-2 p-5 text-left transition-all duration-200 ${
                        selectedCategory === choice.category
                          ? "border-red-500 bg-red-50/60 shadow-sm"
                          : "border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div
                            className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all ${
                              selectedCategory === choice.category
                                ? "border-red-500 bg-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {selectedCategory === choice.category && (
                              <div className="h-2 w-2 rounded-full bg-white" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{choice.title}</div>
                            <div className="mt-0.5 text-xs text-gray-500">{choice.description}</div>
                          </div>
                        </div>
                        <span className="shrink-0 rounded-lg bg-gray-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                          {choice.category === "mobile_money" ? "MoMo" : "Card"}
                        </span>
                      </div>
                      <div className="mt-3 ml-8 text-xs text-gray-400">
                        via <span className="font-medium text-gray-600">{choice.routeLabel}</span>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                    {supportsMobileMoneyForCountry(buyerCountry)
                      ? "No supported payment method is available for this offer and your region."
                      : "This offer does not currently support a card method for your region."}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep("amount")}
                  className="rounded-xl"
                >
                  Back
                </Button>
                <Button
                  onClick={() => {
                    if (selectedCategory === "mobile_money") {
                      setStep("mobile_money");
                    } else {
                      setStep("review");
                    }
                  }}
                  disabled={!canContinuePayment}
                  className="rounded-xl bg-red-500 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-600 disabled:opacity-40"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════ STEP: MOBILE MONEY DETAILS ═══════════════════════════ */}
          {step === "mobile_money" && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-100 p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
                  Mobile money details
                </p>
                <p className="text-sm text-gray-500 mb-5">
                  Select your provider and enter the number to receive the payment prompt.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">Provider</label>
                    {networkProviders.length > 0 ? (
                      <Select
                        value={selectedNetworkCode}
                        onValueChange={setSelectedNetworkCode}
                      >
                        <SelectTrigger className="w-full rounded-xl">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent position="popper" className="z-[9999]">
                          {networkProviders.map((p) => (
                            <SelectItem key={p.code} value={p.code}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-red-500">
                        No mobile money providers available for your region.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">Phone number</label>
                    <Input
                      type="tel"
                      placeholder={getPhonePlaceholder(buyerCountry)}
                      value={mobileMoneyPhone}
                      onChange={(e) => setMobileMoneyPhone(e.target.value)}
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep("payment")}
                  className="rounded-xl"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep("review")}
                  disabled={!mobileMoneyDetailsValid}
                  className="rounded-xl bg-red-500 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-600 disabled:opacity-40"
                >
                  Review Order
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════ STEP: REVIEW ═══════════════════════════ */}
          {step === "review" && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-100 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
                  Order summary
                </p>
                <SummaryRow label="You pay" value={`${buyAmount} ${targetCurrency}`} />
                {feePreview?.fee_applied && feePreview.charge_amount > 0 && (
                  <SummaryRow
                    label={`Fee (${feePreview.charge_percentage || "0"}%)`}
                    value={`${feePreview.charge_amount.toFixed(2)} ${targetCurrency}`}
                  />
                )}
                {feePreview?.fee_applied && feePreview.charge_amount > 0 && (
                  <SummaryRow
                    label="Total charged"
                    value={`${feePreview.total_amount.toFixed(2)} ${targetCurrency}`}
                    accent
                  />
                )}
                <SummaryRow
                  label="You receive"
                  value={`${convertedAmount ? (convertedAmount < 0.01 ? convertedAmount.toFixed(6) : convertedAmount.toFixed(2)) : "—"} AKL`}
                  accent
                />
                <SummaryRow label="Rate" value={`1 AKL = ${request?.conversion_rate_usd} USD`} />
                <SummaryRow
                  label="Payment method"
                  value={
                    selectedCategory === "mobile_money"
                      ? networkProviders.find((p) => p.code === selectedNetworkCode)?.name || "Mobile Money"
                      : "Card"
                  }
                />
                {routePreview && <SummaryRow label="Collection route" value={routePreview} />}
                <SummaryRow label="Seller settlement" value={targetCurrency} />
                {request?.trading_name && (
                  <SummaryRow label="Seller" value={request.trading_name} />
                )}
              </div>

              {submissionError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {submissionError}
                </div>
              )}

              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
                <p className="text-xs text-amber-700">
                  {selectedCategory === "mobile_money"
                    ? "You will receive a payment prompt on your device. Please approve it to complete the trade."
                    : "You will be redirected to a secure payment page to complete your card payment."}
                </p>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    setStep(selectedCategory === "mobile_money" ? "mobile_money" : "payment")
                  }
                  className="rounded-xl"
                >
                  Back
                </Button>
                <Button
                  onClick={submitOrder}
                  disabled={isSubmitting}
                  className="rounded-xl bg-red-500 px-10 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-red-600 disabled:opacity-40"
                >
                  Place Order
                </Button>
              </div>
            </div>
          )}

          {/* ═══════════════════════════ STEP: PROCESSING ═══════════════════════════ */}
          {step === "processing" && (
            <div className="flex flex-col items-center justify-center py-16">
              <LoadingIndicator />
              <p className="mt-6 text-sm font-medium text-gray-600">
                Initiating your payment...
              </p>
              <p className="mt-1 text-xs text-gray-400">
                This may take a few seconds
              </p>
            </div>
          )}

          {/* ═══════════════════════════ STEP: RESULT ═══════════════════════════ */}
          {step === "result" && (
            <div className="space-y-6">
              {/* Status banner */}
              <div
                className={`rounded-2xl p-6 text-center ${
                  providerPayment
                    ? "border border-emerald-100 bg-emerald-50/60"
                    : "border border-red-100 bg-red-50/60"
                }`}
              >
                <div className="mb-3 flex justify-center">
                  {providerPayment ? (
                    <CheckCircledIcon className="h-12 w-12 text-emerald-500" width={48} height={48} />
                  ) : (
                    <CrossCircledIcon className="h-12 w-12 text-red-500" width={48} height={48} />
                  )}
                </div>
                <h3
                  className={`text-lg font-bold ${
                    providerPayment ? "text-emerald-800" : "text-red-800"
                  }`}
                >
                  {isApproveOnPhone
                    ? "Approve Payment on Your Device"
                    : hasRedirect
                      ? "Complete Your Card Payment"
                      : providerPayment
                        ? "Order Submitted"
                        : "Order Created"}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {isApproveOnPhone
                    ? "A payment prompt has been sent to your device. Please approve it to complete the trade."
                    : hasRedirect
                      ? "Click the button below to continue to the secure payment page."
                      : "Your order has been submitted and is being processed."}
                </p>
              </div>

              {/* Order details */}
              <div className="rounded-2xl border border-gray-100 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
                  Order details
                </p>
                <SummaryRow
                  label="Reference"
                  value={orderResponse?.request_id || providerPayment?.transaction_reference || "—"}
                />
                <SummaryRow label="Status" value={providerPayment?.status || orderResponse?.status || "Pending"} />
                <SummaryRow
                  label="Amount"
                  value={`${orderResponse?.amount || buyAmount} ${orderResponse?.currency_code || targetCurrency}`}
                />
                <SummaryRow
                  label="AKL"
                  value={`${orderResponse?.amount_in_lumi || convertedAmount?.toFixed(2) || "—"} AKL`}
                />
                <SummaryRow label="Settlement currency" value={sellerSettlementCurrency || "—"} />
                {providerPayment?.provider && (
                  <SummaryRow label="Provider" value={providerPayment.provider} />
                )}
                {providerPayment?.collection_route && (
                  <SummaryRow label="Route" value={providerPayment.collection_route} />
                )}
              </div>

              {/* Deposit verification result */}
              {depositCheckStatus !== "idle" && (
                <div
                  className={`rounded-xl border p-4 text-sm ${
                    depositCheckStatus === "completed"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : depositCheckStatus === "failed"
                        ? "border-red-200 bg-red-50 text-red-700"
                        : depositCheckStatus === "pending"
                          ? "border-amber-200 bg-amber-50 text-amber-700"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                  }`}
                >
                  {depositCheckStatus === "checking"
                    ? "Verifying your payment..."
                    : depositCheckMessage}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                {hasRedirect && (
                  <Button
                    onClick={() =>
                      window.open(providerPayment.redirect_url, "_blank", "noopener,noreferrer")
                    }
                    className="rounded-xl bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                  >
                    Continue to Payment
                  </Button>
                )}
                {depositCheckStatus === "completed" ? (
                  <Button
                    onClick={handleClose}
                    className="rounded-xl bg-emerald-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                  >
                    Done
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={handleClose}
                      variant="outline"
                      className="rounded-xl"
                    >
                      Close
                    </Button>
                    {isApproveOnPhone && (
                      <Button
                        onClick={async () => {
                          const orderId = orderResponse?.id;
                          if (!orderId) return;
                          setDepositCheckStatus("checking");
                          setDepositCheckMessage("");
                          try {
                            const result = await checkDepositStatus(orderId);
                            if (result.status === "Completed") {
                              setDepositCheckStatus("completed");
                              setDepositCheckMessage("Payment confirmed and settled.");
                            } else if (result.status === "Failed") {
                              setDepositCheckStatus("failed");
                              setDepositCheckMessage(result.message || "Payment failed.");
                            } else {
                              setDepositCheckStatus("pending");
                              setDepositCheckMessage(result.message || "Payment is still being processed. Please wait.");
                            }
                          } catch {
                            setDepositCheckStatus("failed");
                            setDepositCheckMessage("Unable to verify payment. Please try again.");
                          }
                        }}
                        disabled={depositCheckStatus === "checking"}
                        className="rounded-xl bg-red-500 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-600 disabled:opacity-40"
                      >
                        {depositCheckStatus === "checking" ? "Verifying..." : "I have Paid"}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddTradeOrders;
